import { Button, Color, FormField, PopUp, PopUpProps, Spacing, Text, TextInput } from 'zens-storybook'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { ValidationError, object, string } from 'yup'

const styles = StyleSheet.create({
  container: {
    maxWidth: 600,
  },
  text: {
    marginTop: Spacing.L,
    marginBottom: Spacing.L,
  },
  bottom: {
    marginTop: Spacing.L,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  email: {
    marginTop: 0,
  },
})

interface Props extends PopUpProps {
  entity: string
  onDelete?: (email?: string) => Promise<void>
  protectEmail?: boolean
}
const NavigationDelete = (props: Props) => {
  const { style, entity, onClose, onDelete, protectEmail, ...otherProps } = props
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isDeleting, setIsDeleting] = useState(false)

  const confirm = async () => {
    setIsDeleting(true)
    if (protectEmail) {
      try {
        const schema = object({
          email: string().required(t('Email address is required')).email(t('Invalid email address')),
        })
        schema.validateSync({ email, remember: false })
      } catch (error) {
        const { message, path } = error as ValidationError
        setErrors({ [path as string]: message })
        return
      }
      await onDelete?.(email)
    } else {
      await onDelete?.()
    }
    setIsDeleting(false)
  }

  return (
    <PopUp onClose={onClose} style={[styles.container, style]} {...otherProps}>
      <Text level={2}>{t('Delete {{entity}}', { entity })}</Text>
      <Text style={styles.text}>
        <Trans>You are about to delete this {{ entity }} permanently. This action cannot be undone.</Trans>
        {protectEmail && (
          <Text level={5}>
            {' '}
            <Trans>Please enter your email address as a confirmation that you want to proceed.</Trans>
          </Text>
        )}
      </Text>
      {protectEmail && (
        <FormField error={errors.email} style={styles.email}>
          <TextInput
            placeholder={t('Enter your email address')}
            value={email}
            onChangeText={setEmail}
            error={!!errors?.email}
            testID="NavigationDelete.Email"
            disabled={isDeleting}
          />
        </FormField>
      )}
      <View style={styles.bottom}>
        <Button
          left={isDeleting ? <ActivityIndicator color={Color.premiumBlack} /> : 'trash'}
          title={t('Delete {{entity}}', { entity })}
          size="L"
          danger
          onPress={() => !isDeleting && confirm()}
          testID="NavigationDelete.Confirm"
        />
        <Button
          title={t('Cancel')}
          size="L"
          onPress={() => !isDeleting && onClose?.()}
          testID="NavigationDelete.Cancel"
        />
      </View>
    </PopUp>
  )
}

export default NavigationDelete
